import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";

import {
  computeHeadingTicks,
  normalizeHeading,
  type TimedPoint,
} from "@/lib/replay-core";
import type { HudConfig } from "@/hooks/useHudConfig";
import {
  CHECKPOINT_PENDING_COLOR,
  colorForLap,
  type LapPassTimes,
} from "@/lib/lap-tracking";

interface WeatherSnapshot {
  temperature_c?: number | null;
  wind_kmh?: number | null;
  wind_direction_deg?: number | null;
  cloud_pct?: number | null;
}

interface StickAxes {
  /** Left stick X (yaw). Normalized to -1..+1. */
  lx: number;
  /** Left stick Y (throttle). Normalized to -1..+1, +1 = up. */
  ly: number;
  /** Right stick X (roll). Normalized to -1..+1. */
  rx: number;
  /** Right stick Y (pitch). Normalized to -1..+1, +1 = up. */
  ry: number;
}

interface HudWidgetsProps {
  config: HudConfig;
  point: TimedPoint | null;
  elapsed: number;
  total: number;
  cumulativeDistance: number;
  weather?: WeatherSnapshot | null;
  /** Full route in lat/lon for the minimap. */
  routeLatLon?: Array<[number, number]>;
  /** Checkpoints for the minimap. */
  checkpoints?: Array<{
    key: string | number | null;
    lat: number;
    lon: number;
  }>;
  /** Per-lap pass times for color cycling. */
  lapPasses?: LapPassTimes;
  /** Index of the most recent route sample at the current elapsed time. */
  progressIndex: number;
  /** Full telemetry samples — needed to compute derivatives (G, accel). */
  points?: TimedPoint[];
  /** Real joystick axes (from User Inputs → Sticks), normalized to -1..+1. */
  stickAxes?: StickAxes | null;
  /** Running max speed up to current elapsed time (m/s). */
  maxSpeedMps?: number;
}

function formatSigned(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value))
    return "--";
  const n = Number(value);
  return `${n >= 0 ? "+" : ""}${n.toFixed(digits)}`;
}

function useElementSize<T extends HTMLElement>(): [
  React.RefObject<T>,
  { width: number; height: number },
] {
  const ref = useRef<T>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useLayoutEffect(() => {
    if (!ref.current) return;
    const el = ref.current;
    const update = () =>
      setSize({ width: el.clientWidth, height: el.clientHeight });
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, size];
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

function CompassRibbon({ heading }: { heading: number }) {
  const [ref, size] = useElementSize<HTMLDivElement>();
  const ticks = computeHeadingTicks(heading, size.width || 360);
  return (
    <div className="rhud-compass">
      <div className="rhud-compass-scale" ref={ref}>
        {ticks.map((t, i) => (
          <div
            key={i}
            className={`rhud-compass-tick ${t.major ? "major" : "minor"}`}
            style={{ left: `${t.left}px` }}
            data-label={t.label || undefined}
          />
        ))}
      </div>
      <div className="rhud-compass-pointer" />
      <div className="rhud-compass-value">
        <strong>{String(heading).padStart(3, "0")}°</strong>
        <small>HDG</small>
      </div>
    </div>
  );
}

function ArtificialHorizon({
  pitch,
  roll,
}: {
  pitch: number;
  roll: number;
}) {
  // Drones routinely fly at ±60°+ pitch/roll, so the ladder covers ±60°
  // with a softer per-degree scale (2.2 px/°) that keeps extremes on-screen.
  const PX_PER_DEG = 2.2;
  return (
    <div className="rhud-horizon">
      <div className="rhud-horizon-mask">
        <div
          className="rhud-horizon-world"
          style={{
            transform: `translateY(${pitch * PX_PER_DEG}px) rotate(${-roll}deg)`,
          }}
        >
          <div className="rhud-horizon-sky" />
          <div className="rhud-horizon-ground" />
          <div className="rhud-horizon-line" />
          <div className="rhud-horizon-ladder">
            {[-60, -45, -30, -20, -10, 10, 20, 30, 45, 60].map((offset) => (
              <div
                key={offset}
                className={`rhud-horizon-mark ${
                  offset < 0 ? "negative" : "positive"
                } ${Math.abs(offset) % 30 === 0 ? "major" : ""}`}
                style={{ top: `calc(50% + ${offset * PX_PER_DEG}px)` }}
              >
                <span>{Math.abs(offset)}</span>
                <i />
                <span>{Math.abs(offset)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="rhud-horizon-reticle">
          <span />
          <span />
          <span />
        </div>
        {/* Roll pointer arc + tick around the rim */}
        <div className="rhud-horizon-rollarc">
          <div
            className="rhud-horizon-rollpointer"
            style={{ transform: `translateX(-50%) rotate(${-roll}deg)` }}
          >
            <i />
          </div>
        </div>
      </div>
      <div className="rhud-horizon-readout">
        <span>
          <strong>{formatSigned(pitch, 0)}°</strong>
          <small>PITCH</small>
        </span>
        <span>
          <strong>{formatSigned(roll, 0)}°</strong>
          <small>ROLL</small>
        </span>
      </div>
    </div>
  );
}

interface BarConfig {
  /** Current value mapped into [min..max]. */
  value: number;
  min: number;
  max: number;
  /** Center-anchored bar (e.g. vertical rate ± 0). */
  bipolar?: boolean;
}

function SpeedTape({
  value,
  unit,
  min = 0,
  max = 250,
  step = 10,
  accent = "var(--primary)",
  label = "SPEED",
  maxValue,
}: {
  value: number;
  unit: string;
  min?: number;
  max?: number;
  step?: number;
  accent?: string;
  label?: string;
  maxValue?: number;
}) {
  const clamped = clamp(value, min, max);
  const pxPerUnit = 1.6; // how many px per unit of value on the tape
  // Generate ticks around the current value (±4 steps)
  const ticks: Array<{ v: number; offset: number; major: boolean }> = [];
  const base = Math.round(clamped / step) * step;
  for (let i = -4; i <= 4; i += 1) {
    const v = base + i * step;
    if (v < min - step || v > max + step) continue;
    const offset = (v - clamped) * pxPerUnit;
    ticks.push({ v, offset, major: true });
    // add minor tick halfway
    if (i < 4) {
      const vm = v + step / 2;
      if (vm <= max) {
        ticks.push({ v: vm, offset: (vm - clamped) * pxPerUnit, major: false });
      }
    }
  }
  const showMax = maxValue != null && Number.isFinite(maxValue) && maxValue > 0;
  return (
    <div className="rhud-tape">
      <span className="rhud-chip-label">{label}</span>
      <div className="rhud-tape-body">
        <div className="rhud-tape-scale">
          {ticks.map((t, i) => (
            <div
              key={i}
              className={`rhud-tape-tick${t.major ? " major" : ""}`}
              style={{ transform: `translateY(${-t.offset}px)` }}
            >
              {t.major && <span>{Math.round(t.v)}</span>}
            </div>
          ))}
          <div className="rhud-tape-center-line" />
        </div>
        <div className="rhud-tape-readout" style={{ borderColor: accent }}>
          <strong style={{ color: accent }}>{Math.round(value)}</strong>
          <small>{unit}</small>
        </div>
      </div>
      {showMax && (
        <div className="rhud-tape-max">
          <small>MAX</small>
          <strong>{Math.round(maxValue!)}</strong>
          <small>{unit}</small>
        </div>
      )}
    </div>
  );
}

function VSpeedTape({
  value,
  unit = "m/s",
  range = 10,
  label = "VS",
}: {
  value: number;
  unit?: string;
  range?: number;
  label?: string;
}) {
  const clamped = clamp(value, -range, range);
  const pct = clamped / range; // -1..1
  const accent =
    value > 0.5
      ? "var(--success)"
      : value < -0.5
        ? "var(--info)"
        : "var(--text-muted)";
  // Pointer Y in 0..1 space: 0.5 at center, top = climb
  const pointerY = 50 - pct * 42;
  return (
    <div className="rhud-vstape">
      <span className="rhud-chip-label">{label}</span>
      <div className="rhud-vstape-body">
        <svg viewBox="0 0 34 100" className="rhud-vstape-svg">
          {/* Scale backdrop */}
          <rect x={4} y={6} width={8} height={88} className="rhud-vstape-track" rx={2} />
          {/* Scale ticks */}
          {[-range, -range / 2, 0, range / 2, range].map((t) => {
            const y = 50 - (t / range) * 42;
            return (
              <g key={t}>
                <line x1={4} y1={y} x2={14} y2={y} className="rhud-vstape-tick" />
                <text x={16} y={y + 1.4} className="rhud-vstape-label">
                  {t > 0 ? `+${t}` : t}
                </text>
              </g>
            );
          })}
          {/* Zero line */}
          <line x1={4} y1={50} x2={14} y2={50} className="rhud-vstape-zero" />
          {/* Climb bar */}
          {pct > 0 && (
            <rect x={4} y={pointerY} width={8} height={50 - pointerY} fill={accent} opacity="0.55" />
          )}
          {/* Descent bar */}
          {pct < 0 && (
            <rect x={4} y={50} width={8} height={pointerY - 50} fill={accent} opacity="0.55" />
          )}
          {/* Pointer arrow */}
          <polygon
            points={`0,${pointerY} 4,${pointerY - 3.5} 4,${pointerY + 3.5}`}
            fill={accent}
          />
          <line x1={4} y1={pointerY} x2={14} y2={pointerY} stroke={accent} strokeWidth="1.4" />
        </svg>
        <div className="rhud-vstape-readout">
          <strong style={{ color: accent }}>{formatSigned(value, 1)}</strong>
          <small>{unit}</small>
        </div>
      </div>
    </div>
  );
}

function StatChip({
  label,
  value,
  unit,
  accent,
  bar,
}: {
  label: string;
  value: string;
  unit?: string;
  accent?: string;
  bar?: BarConfig;
}) {
  const renderBar = bar
    ? (() => {
        const span = Math.max(bar.max - bar.min, 1e-6);
        if (bar.bipolar) {
          // Center anchor; positive fills right, negative fills left
          const pct = clamp((bar.value / Math.max(bar.max, -bar.min)) * 50, -50, 50);
          const left = pct >= 0 ? 50 : 50 + pct;
          const width = Math.abs(pct);
          return (
            <div className="rhud-chip-bar rhud-chip-bar--bipolar">
              <i style={{ left: "50%" }} />
              <span
                style={{
                  left: `${left}%`,
                  width: `${width}%`,
                  background: accent ?? "var(--primary)",
                }}
              />
            </div>
          );
        }
        const pct = clamp(((bar.value - bar.min) / span) * 100, 0, 100);
        return (
          <div className="rhud-chip-bar">
            <span
              style={{
                width: `${pct}%`,
                background: accent ?? "var(--primary)",
              }}
            />
          </div>
        );
      })()
    : null;

  return (
    <div
      className={`rhud-chip${bar ? " has-bar" : ""}`}
      style={accent ? { borderColor: accent } : undefined}
    >
      <span className="rhud-chip-label">{label}</span>
      <strong style={accent ? { color: accent } : undefined}>
        {value}
        {unit && <small>{unit}</small>}
      </strong>
      {renderBar}
    </div>
  );
}

interface GLoad {
  lateral: number; // +right / -left (turn-induced centripetal)
  longitudinal: number; // +accel / -decel (forward/back)
  vertical: number; // 1.0 baseline, 0 = freefall, 2+ = pull-up
  trail: Array<{ lat: number; lon: number }>; // recent (lateral, longitudinal) samples
  peakLat: number;
  peakLon: number;
  peakVert: number;
}

const G_EARTH = 9.80665;
const LAT_METERS_PER_DEG = 111320;
const lonMetersPerDeg = (latDeg: number) =>
  111320 * Math.cos((latDeg * Math.PI) / 180);

function computeGLoad(
  points: TimedPoint[] | undefined,
  progressIndex: number,
): GLoad {
  const empty: GLoad = {
    lateral: 0,
    longitudinal: 0,
    vertical: 1,
    trail: [],
    peakLat: 0,
    peakLon: 0,
    peakVert: 1,
  };
  if (!points || points.length < 3 || progressIndex < 2) return empty;

  const windowStart = Math.max(1, progressIndex - 30);
  const trail: Array<{ lat: number; lon: number }> = [];
  let peakLat = 0;
  let peakLon = 0;
  let peakVert = 1;

  // Compute horizontal accel in the world frame (North, East) via centered
  // differences of GPS-derived velocity, then rotate into the DIRECTION-OF-
  // MOTION frame so lateral = perpendicular-to-motion (turn-induced) and
  // longitudinal = along-motion (accel/brake). The window around each sample
  // is time-based (~0.3s on each side) so noise-amplification from tiny
  // sample-to-sample dt values doesn't blow up the derivative — at 7.5 Hz a
  // 1-step window would multiply position jitter by ~7, producing nonsense
  // 50G peaks on otherwise tame flights.
  const WINDOW_SEC = 0.3;
  const findPrev = (i: number): number => {
    let j = Math.max(0, i - 1);
    const tb = points[i].elapsed_seconds;
    while (j > 0 && tb - points[j].elapsed_seconds < WINDOW_SEC) j -= 1;
    return j;
  };
  const findNext = (i: number): number => {
    // Clamp the starting index so the last sample doesn't overshoot the array
    // — that used to crash the HUD (and black-out the page) whenever playback
    // reached the final frame of a flight.
    let j = Math.min(points.length - 1, i + 1);
    const tb = points[i].elapsed_seconds;
    while (
      j < points.length - 1 &&
      points[j].elapsed_seconds - tb < WINDOW_SEC
    )
      j += 1;
    return j;
  };

  const sampleG = (i: number): { lat: number; lon: number; vert: number } => {
    const iPrev = findPrev(i);
    const iNext = findNext(i);
    const a = points[iPrev];
    const b = points[i];
    const c = points[iNext];
    const dt_ab = Math.max(b.elapsed_seconds - a.elapsed_seconds, 1e-3);
    const dt_bc = Math.max(c.elapsed_seconds - b.elapsed_seconds, 1e-3);
    const lonScale = lonMetersPerDeg(b.latitude);
    const vN_ab = ((b.latitude - a.latitude) * LAT_METERS_PER_DEG) / dt_ab;
    const vE_ab = ((b.longitude - a.longitude) * lonScale) / dt_ab;
    const vN_bc = ((c.latitude - b.latitude) * LAT_METERS_PER_DEG) / dt_bc;
    const vE_bc = ((c.longitude - b.longitude) * lonScale) / dt_bc;
    const avgDt = (dt_ab + dt_bc) / 2;
    const aN = (vN_bc - vN_ab) / avgDt;
    const aE = (vE_bc - vE_ab) / avgDt;
    // Direction of motion at b (average of the two intervals). Fall back to
    // drone yaw when horizontal speed is too low to be reliable.
    const vN = (vN_ab + vN_bc) / 2;
    const vE = (vE_ab + vE_bc) / 2;
    const speedH = Math.hypot(vN, vE);
    let uN: number;
    let uE: number;
    if (speedH > 1) {
      uN = vN / speedH;
      uE = vE / speedH;
    } else {
      const yawRad = ((b.yaw_deg ?? 0) * Math.PI) / 180;
      // yaw=0 → heading north; yaw 90° → east. Convert heading to (N, E) unit.
      uN = Math.cos(yawRad);
      uE = Math.sin(yawRad);
    }
    // Project world-frame accel onto motion frame.
    const aLong = aN * uN + aE * uE;
    // Lateral (+ = drone accelerates to its RIGHT relative to motion).
    // Derived from rotating motion 90° CW: right = (-uE, uN) in (N, E).
    const aLat = aE * uN - aN * uE;
    // Vertical: baseline 1g, modified by d(vertical_rate)/dt across the same
    // wide window — matches the horizontal smoothing so the three axes are
    // consistent.
    const vrA = a.vertical_rate_mps ?? 0;
    const vrC = c.vertical_rate_mps ?? 0;
    const dtAC = Math.max(c.elapsed_seconds - a.elapsed_seconds, 1e-3);
    const vertG = 1 + (vrC - vrA) / dtAC / G_EARTH;
    // Clamp to a physically sane envelope — racing drones can pull ~10G, so
    // anything well past that is numerical noise we don't want to display.
    const clampG = (g: number, lim: number) =>
      Math.max(-lim, Math.min(lim, g));
    return {
      lat: clampG(aLat / G_EARTH, 15),
      lon: clampG(aLong / G_EARTH, 15),
      vert: clampG(vertG, 15),
    };
  };

  for (let i = windowStart; i <= progressIndex; i += 1) {
    const s = sampleG(i);
    trail.push({ lat: s.lat, lon: s.lon });
    if (Math.abs(s.lat) > Math.abs(peakLat)) peakLat = s.lat;
    if (Math.abs(s.lon) > Math.abs(peakLon)) peakLon = s.lon;
    if (Math.abs(s.vert - 1) > Math.abs(peakVert - 1)) peakVert = s.vert;
  }

  // Short (2-sample) moving average on the current G vector keeps response
  // snappy while damping single-frame GPS noise.
  const smoothN = Math.min(2, trail.length);
  let sLat = 0;
  let sLon = 0;
  let sVert = 1;
  if (smoothN > 0) {
    for (let i = trail.length - smoothN; i < trail.length; i += 1) {
      sLat += trail[i].lat;
      sLon += trail[i].lon;
    }
    sLat /= smoothN;
    sLon /= smoothN;
    sVert = sampleG(progressIndex).vert;
  }

  return {
    lateral: sLat,
    longitudinal: sLon,
    vertical: sVert,
    trail,
    peakLat,
    peakLon,
    peakVert,
  };
}

function GMeterScope({ load }: { load: GLoad }) {
  // F1-style readout: one big magnitude + a directional arrow pointing where
  // the pilot feels the G force pushing them. Acceleration pushes the pilot
  // backward, braking pushes forward, turn-right pushes left. So the felt
  // direction is the opposite of the acceleration vector.
  const feltLat = -load.lateral;
  const feltLon = -load.longitudinal;
  const magnitude = Math.hypot(load.lateral, load.longitudinal);
  const peakMagnitude = Math.hypot(load.peakLat, load.peakLon);
  const accent =
    magnitude > 2.2
      ? "var(--danger)"
      : magnitude > 1.4
        ? "var(--warning)"
        : magnitude > 0.6
          ? "var(--primary)"
          : "var(--success)";
  // Arrow angle in degrees: 0° = up (forward), rotate clockwise.
  // Vector (feltLat, feltLon): feltLat>0 = right, feltLon>0 = forward.
  // Screen: forward = up, right = right. angle = atan2(feltLat, feltLon) from +Y (up).
  const arrowAngle =
    magnitude > 0.05
      ? (Math.atan2(feltLat, feltLon) * 180) / Math.PI
      : 0;
  // Arrow length scales with G magnitude (capped at 3g).
  const armLength = clamp(magnitude / 3, 0, 1);

  return (
    <div className="rhud-chip rhud-chip--gmeter">
      <span className="rhud-chip-label">G METER</span>
      <div className="rhud-gscope">
        <svg viewBox="0 0 100 100" className="rhud-gscope-svg">
          {/* Outer ring + reference rings (1g, 2g, 3g) */}
          <circle cx="50" cy="50" r={40} className="rhud-gscope-ring rhud-gscope-ring--outer" />
          <circle cx="50" cy="50" r={40 * (2 / 3)} className="rhud-gscope-ring" />
          <circle cx="50" cy="50" r={40 * (1 / 3)} className="rhud-gscope-ring" />
          {/* Cross axis (subtle) */}
          <line x1={14} y1={50} x2={86} y2={50} className="rhud-gscope-axis" />
          <line x1={50} y1={14} x2={50} y2={86} className="rhud-gscope-axis" />
          {/* Peak marker arc: same angle as historic peak, at outer ring */}
          {peakMagnitude > 0.3 && (() => {
            const peakFeltLat = -load.peakLat;
            const peakFeltLon = -load.peakLon;
            const peakAngle = Math.atan2(peakFeltLat, peakFeltLon);
            const peakR = clamp(peakMagnitude / 3, 0, 1) * 40;
            const px = 50 + Math.sin(peakAngle) * peakR;
            const py = 50 - Math.cos(peakAngle) * peakR;
            return <circle cx={px} cy={py} r={1.8} className="rhud-gscope-peak" />;
          })()}
          {/* Directional arrow pointing where G is felt */}
          {magnitude > 0.1 && (
            <g transform={`rotate(${arrowAngle} 50 50)`}>
              <line
                x1={50}
                y1={50}
                x2={50}
                y2={50 - armLength * 36}
                stroke={accent}
                strokeWidth="3"
                strokeLinecap="round"
              />
              <polygon
                points={`50,${50 - armLength * 36 - 6} 46,${50 - armLength * 36 + 2} 54,${50 - armLength * 36 + 2}`}
                fill={accent}
              />
            </g>
          )}
          {/* Center hub */}
          <circle cx={50} cy={50} r={3} fill="rgba(10,14,20,0.9)" stroke={accent} strokeWidth="0.8" />
          {/* Big magnitude number overlay */}
          <text
            x={50}
            y={82}
            className="rhud-gscope-bignum"
            textAnchor="middle"
            fill={accent}
          >
            {magnitude.toFixed(1)}
          </text>
          <text x={50} y={91} className="rhud-gscope-bigunit" textAnchor="middle">
            G
          </text>
        </svg>
        {peakMagnitude > 0.3 && (
          <div className="rhud-gscope-peakrow">
            <small>PEAK</small>
            <strong>{peakMagnitude.toFixed(1)} G</strong>
          </div>
        )}
      </div>
    </div>
  );
}

function batteryAccent(percent: number | null): string {
  if (percent == null) return "var(--text-muted)";
  if (percent < 15) return "var(--danger)";
  if (percent < 30) return "var(--warning)";
  return "var(--success)";
}

function StickIndicator({
  label,
  x,
  y,
  xLabel,
  yLabel,
}: {
  label: string;
  x: number; // -1..1
  y: number; // -1..1, +1 = up
  xLabel: string;
  yLabel: string;
}) {
  const cx = 50 + clamp(x, -1, 1) * 34;
  const cy = 50 - clamp(y, -1, 1) * 34;
  return (
    <div className="rhud-stick">
      <header>
        <strong>{label}</strong>
      </header>
      <div className="rhud-stick-body">
        <svg viewBox="0 0 100 100" className="rhud-stick-svg">
          <circle cx={50} cy={50} r={42} className="rhud-stick-frame" />
          <circle cx={50} cy={50} r={28} className="rhud-stick-ring" />
          <circle cx={50} cy={50} r={14} className="rhud-stick-ring" />
          <line x1={8} y1={50} x2={92} y2={50} className="rhud-stick-axis" />
          <line x1={50} y1={8} x2={50} y2={92} className="rhud-stick-axis" />
          <circle cx={50} cy={50} r={3.2} className="rhud-stick-center" />
          <circle cx={cx} cy={cy} r={8} className="rhud-stick-knob" />
          <circle cx={cx} cy={cy} r={2.6} className="rhud-stick-knob-dot" />
          <text x={50} y={9} className="rhud-stick-axislabel" textAnchor="middle">
            {yLabel}+
          </text>
          <text x={50} y={96.5} className="rhud-stick-axislabel" textAnchor="middle">
            {yLabel}−
          </text>
          <text x={3} y={52.5} className="rhud-stick-axislabel" textAnchor="start">
            {xLabel}−
          </text>
          <text x={97} y={52.5} className="rhud-stick-axislabel" textAnchor="end">
            {xLabel}+
          </text>
        </svg>
      </div>
    </div>
  );
}

function WeatherBadge({ weather }: { weather: WeatherSnapshot }) {
  const dir = weather.wind_direction_deg;
  return (
    <div className="rhud-chip rhud-chip--weather">
      <span className="rhud-chip-label">WEATHER</span>
      <div className="rhud-weather-grid">
        <div className="rhud-weather-cell rhud-weather-cell--temp">
          <strong>{weather.temperature_c?.toFixed(0) ?? "—"}°</strong>
          <small>TEMP</small>
        </div>
        <div className="rhud-weather-cell">
          <div className="rhud-weather-wind">
            {dir != null && (
              <svg viewBox="0 0 24 24" className="rhud-weather-arrow" style={{ transform: `rotate(${dir}deg)` }}>
                <path d="M12 2 L18 14 L12 11 L6 14 Z" fill="currentColor" />
              </svg>
            )}
            <strong>{weather.wind_kmh?.toFixed(0) ?? "—"}</strong>
            <small>km/h</small>
          </div>
          <small>WIND</small>
        </div>
        <div className="rhud-weather-cell">
          <strong>{weather.cloud_pct?.toFixed(0) ?? "—"}%</strong>
          <small>CLOUD</small>
        </div>
      </div>
    </div>
  );
}

function MinimapPanel({
  routeLatLon,
  checkpoints,
  lapPasses,
  progressIndex,
  elapsed,
  total,
}: {
  routeLatLon: Array<[number, number]>;
  checkpoints: Array<{ key: string | number | null; lat: number; lon: number }>;
  lapPasses?: LapPassTimes;
  progressIndex: number;
  elapsed: number;
  total: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [size, setSize] = useState({ w: 200, h: 130 });

  useLayoutEffect(() => {
    if (!canvasRef.current) return;
    const update = () => {
      const el = canvasRef.current?.parentElement;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setSize({ w: rect.width, h: rect.height });
    };
    update();
    const ro = new ResizeObserver(update);
    if (canvasRef.current.parentElement)
      ro.observe(canvasRef.current.parentElement);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !routeLatLon.length) return;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = size.w * dpr;
    canvas.height = size.h * dpr;
    canvas.style.width = `${size.w}px`;
    canvas.style.height = `${size.h}px`;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size.w, size.h);

    let minLat = Infinity,
      maxLat = -Infinity,
      minLon = Infinity,
      maxLon = -Infinity;
    for (const [lat, lon] of routeLatLon) {
      if (lat < minLat) minLat = lat;
      if (lat > maxLat) maxLat = lat;
      if (lon < minLon) minLon = lon;
      if (lon > maxLon) maxLon = lon;
    }
    for (const cp of checkpoints) {
      if (cp.lat < minLat) minLat = cp.lat;
      if (cp.lat > maxLat) maxLat = cp.lat;
      if (cp.lon < minLon) minLon = cp.lon;
      if (cp.lon > maxLon) maxLon = cp.lon;
    }
    const latRange = Math.max(maxLat - minLat, 1e-6);
    const lonRange = Math.max(maxLon - minLon, 1e-6);
    const pad = 8;
    const innerW = size.w - pad * 2;
    const innerH = size.h - pad * 2;
    const project = ([lat, lon]: [number, number]): [number, number] => {
      const x = pad + ((lon - minLon) / lonRange) * innerW;
      const y = pad + (1 - (lat - minLat) / latRange) * innerH;
      return [x, y];
    };

    // Future segment: desaturated tactical blue (course preview)
    if (progressIndex < routeLatLon.length - 1) {
      ctx.beginPath();
      const [sx, sy] = project(routeLatLon[progressIndex]);
      ctx.moveTo(sx, sy);
      for (let i = progressIndex + 1; i < routeLatLon.length; i += 1) {
        const [px, py] = project(routeLatLon[i]);
        ctx.lineTo(px, py);
      }
      ctx.strokeStyle = "rgba(90, 127, 168, 0.7)";
      ctx.lineWidth = 1.4;
      ctx.setLineDash([3, 3]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Past segment: bright orange (flown)
    if (progressIndex > 0) {
      ctx.beginPath();
      const [sx, sy] = project(routeLatLon[0]);
      ctx.moveTo(sx, sy);
      for (let i = 1; i <= progressIndex; i += 1) {
        const [px, py] = project(routeLatLon[i]);
        ctx.lineTo(px, py);
      }
      ctx.strokeStyle = "#FF6B1A";
      ctx.lineWidth = 2;
      ctx.stroke();
    }

    // Checkpoints
    for (const cp of checkpoints) {
      const [cx, cy] = project([cp.lat, cp.lon]);
      const passes = cp.key !== null ? lapPasses?.get(cp.key) ?? [] : [];
      const lapsDone = passes.filter((t) => t <= elapsed).length;
      const color = lapsDone > 0 ? colorForLap(lapsDone) : CHECKPOINT_PENDING_COLOR;
      ctx.beginPath();
      ctx.arc(cx, cy, 4, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.globalAlpha = 0.85;
      ctx.fill();
      ctx.globalAlpha = 1;
      ctx.strokeStyle = "rgba(232, 236, 241, 0.6)";
      ctx.lineWidth = 0.8;
      ctx.stroke();
    }

    // Drone marker
    if (progressIndex >= 0 && progressIndex < routeLatLon.length) {
      const [dx, dy] = project(routeLatLon[progressIndex]);
      ctx.beginPath();
      ctx.arc(dx, dy, 5, 0, Math.PI * 2);
      ctx.fillStyle = "#E8ECF1";
      ctx.fill();
      ctx.lineWidth = 2;
      ctx.strokeStyle = "#FF6B1A";
      ctx.stroke();
    }
  }, [routeLatLon, checkpoints, lapPasses, progressIndex, elapsed, size]);

  return (
    <div className="rhud-minimap">
      <header>
        <strong>MAP</strong>
        <small>
          {Math.floor(elapsed)}s · {Math.floor(total)}s
        </small>
      </header>
      <div className="rhud-minimap-canvas">
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}

export function HudWidgets({
  config,
  point,
  elapsed,
  total,
  cumulativeDistance,
  weather,
  routeLatLon,
  checkpoints,
  lapPasses,
  progressIndex,
  points,
  stickAxes,
  maxSpeedMps,
}: HudWidgetsProps) {
  const heading = normalizeHeading(point?.yaw_deg ?? 0);
  const pitch = point?.pitch_deg ?? 0;
  const roll = point?.roll_deg ?? 0;
  const speedKmh = (point?.speed_mps ?? 0) * 3.6;
  const maxSpeedKmh =
    maxSpeedMps != null && Number.isFinite(maxSpeedMps) ? maxSpeedMps * 3.6 : 0;
  const vRate = point?.vertical_rate_mps ?? 0;
  const fuel = point?.fuel ?? null;
  const fuelCap = point?.fuel_capacity ?? null;

  const batteryPct = useMemo(() => {
    if (fuel == null) return null;
    const f = Number(fuel);
    const cap = fuelCap == null ? null : Number(fuelCap);
    if (f >= 0 && f <= 1 && (cap == null || cap > 1)) return f * 100;
    if (cap == null || cap === 0) return f;
    return (f / cap) * 100;
  }, [fuel, fuelCap]);

  // G-meter: derive lateral/longitudinal/vertical G from telemetry derivatives
  const gLoad = useMemo(
    () => computeGLoad(points, progressIndex),
    [points, progressIndex],
  );

  // Stick inputs: prefer real axes from the User Inputs (tx16s) stream.
  // Fall back to telemetry-derived approximations when no input data exists.
  const rightX = stickAxes?.rx ?? clamp(roll / 45, -1, 1);
  const rightY = stickAxes?.ry ?? clamp(-pitch / 45, -1, 1);
  const leftX = stickAxes?.lx ?? 0;
  const leftY = stickAxes?.ly ?? clamp(vRate / 8, -1, 1);

  const formattedDistance =
    cumulativeDistance >= 1000
      ? `${(cumulativeDistance / 1000).toFixed(2)} km`
      : `${cumulativeDistance.toFixed(0)} m`;

  return (
    <div className="rhud-overlay">
      {config.compass && (
        <div className="rhud-zone rhud-zone-top-center">
          <CompassRibbon heading={heading} />
        </div>
      )}

      {config.horizon && (
        <div className="rhud-zone rhud-zone-center">
          <ArtificialHorizon pitch={pitch} roll={roll} />
        </div>
      )}

      <div className="rhud-zone rhud-zone-top-left">
        {(config.speed || config.vspeed) && (
          <div className="rhud-tape-row">
            {config.speed && (
              <SpeedTape
                label="SPEED"
                value={speedKmh}
                unit="km/h"
                min={0}
                max={300}
                step={20}
                accent="var(--primary)"
                maxValue={maxSpeedKmh}
              />
            )}
            {config.vspeed && <VSpeedTape label="V/S" value={vRate} range={10} />}
          </div>
        )}
        {config.distance && (
          <StatChip label="DISTANCE" value={formattedDistance} />
        )}
      </div>

      <div className="rhud-zone rhud-zone-top-right">
        {config.battery && (
          <StatChip
            label="BATTERY"
            value={batteryPct != null ? `${batteryPct.toFixed(0)}` : "—"}
            unit={batteryPct != null ? "%" : ""}
            accent={batteryAccent(batteryPct)}
            bar={
              batteryPct != null
                ? { value: batteryPct, min: 0, max: 100 }
                : undefined
            }
          />
        )}
        {config.gmeter && <GMeterScope load={gLoad} />}
        {config.weather && weather && <WeatherBadge weather={weather} />}
      </div>

      {config.inputs && (
        <div className="rhud-zone rhud-zone-bottom-center">
          <StickIndicator
            label="LEFT"
            x={leftX}
            y={leftY}
            xLabel="YAW"
            yLabel="THR"
          />
          <StickIndicator
            label="RIGHT"
            x={rightX}
            y={rightY}
            xLabel="ROLL"
            yLabel="PITCH"
          />
        </div>
      )}

      {config.minimap && routeLatLon && routeLatLon.length > 1 && (
        <div className="rhud-zone rhud-zone-bottom-right">
          <MinimapPanel
            routeLatLon={routeLatLon}
            checkpoints={checkpoints ?? []}
            lapPasses={lapPasses}
            progressIndex={progressIndex}
            elapsed={elapsed}
            total={total}
          />
        </div>
      )}
    </div>
  );
}
